import { Row } from '@tanstack/react-table';
import type { User } from '../Groups';
import { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import StyledButton, { LoadingSpinner } from '../StyledHtmlElements/Button';
import { cn } from '@/lib/utils';
import { useCognito } from '@/hooks/use-cognito';
import { useUser } from '@/hooks/user-user';
import { Checkbox } from '../ui/checkbox';
import SelectUserGroup from '../SelectUserGroup';
import { useToast } from '@/hooks/use-toast';

type UserAttr = {
  group: string[];
  scoutAccess: boolean;
  clubAccess: boolean;
};

const UsermanagmentDropdown = ({ row }: { row: Row<User> }) => {
  const { isGlobalAdmin, teamName, isGroupAdmin } = useUser();
  const [open, setOpen] = useState(false);
  const [scoutAccessCheckbox, setScoutAccessCheckbox] = useState<boolean>(false);
  const [clubAccessCheckbox, setClubAccessCheckbox] = useState<boolean>(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [loadingRow, setLoadingRow] = useState<Array<User['id']>>([]);
  const { toast, dismiss } = useToast();
  const [userAttributes, setUserAttributes] = useState<UserAttr>({
    group: [],
    scoutAccess: false,
    clubAccess: false,
  });
  const userAssignedToAdminGroup = userAttributes.group.some((group) => group.toLowerCase().includes('admin'));
  const { groupList, removeUserFromGroup, deleteUser, addUserToGroup, changeUserAccess } = useCognito();
  const userGroups = row.original.groups;
  const isRowLoading = loadingRow.includes(row.original.id);
  const user = row.original;

  const changeUserConfig = (
    newUserattr: UserAttr,
    currUserAttr: User
    // id: string
  ) => {
    setUserAttributes(() => newUserattr);
    dismiss();

    // check if the new group a user is assigned to is different from the current group the user is in
    const groupHasChanged = !(
      newUserattr.group.every((group) => currUserAttr.groups.includes(group)) &&
      currUserAttr.groups.every((group) => newUserattr.group.includes(group))
    );
    // check if the access rights have changed
    const accessHasChanged =
      newUserattr.scoutAccess !== currUserAttr.hasScoutAccess || newUserattr.clubAccess !== currUserAttr.hasClubAccess;
    const userAssignedToAdminGroup = newUserattr.group.some((group) => group.toLowerCase().includes('groupadmins'));

    // disable the submit button if nothing has changed
    setSubmitButtonDisabled(!groupHasChanged && !accessHasChanged);

    // if the user gets assigned to an admin group, disable and check the checkboxes. Cause any admin group has scout and club access
    if (userAssignedToAdminGroup) {
      setScoutAccessCheckbox(true);
      setClubAccessCheckbox(true);

      setUserAttributes((prev) => ({
        ...prev,
        scoutAccess: true,
        clubAccess: true,
      }));
    }

    // notify the user if no access rights are selected
    if (!newUserattr.scoutAccess && !newUserattr.clubAccess && !userAssignedToAdminGroup) {
      toast({
        title: 'Zugriffsoptionen',
        description: 'Mindestens eine Zugriffsoptionen muss ausgewählt werden.',
      });
    }
  };

  const handleUserGroupChanges = async (user: User) => {
    const { groups: currentUserGroups, id, hasScoutAccess, hasClubAccess } = user;
    setLoadingRow((prev) => [...prev, id]);

    // if the current groups a user is assigned to are removed from the selected groups, remove the user from the group
    const groupsToRemove = currentUserGroups.filter((group) => !userAttributes.group.includes(group));

    if (groupsToRemove.length) {
      const removePromises = groupsToRemove.map((group) => removeUserFromGroup(id, group));

      await Promise.all(removePromises);
    }

    // if the selected groups are not in the current groups, add the user to the group
    const groupsToAdd = userAttributes.group.filter((group) => !currentUserGroups.includes(group));

    if (groupsToAdd.length) {
      const addPromises = groupsToAdd.map((group) => addUserToGroup(id, group));

      await Promise.all(addPromises);
    }

    // if any of the access rights have changed, update the user access
    if (userAttributes.scoutAccess !== hasScoutAccess || userAttributes.clubAccess !== hasClubAccess) {
      await changeUserAccess(id, userAttributes.scoutAccess, userAttributes.clubAccess);
    }

    setLoadingRow((prev) => prev.filter((rowId) => rowId !== id));
    setSubmitButtonDisabled(true);
  };

  const resetAllToDefault = () => {
    setScoutAccessCheckbox(user.hasScoutAccess);
    setClubAccessCheckbox(user.hasClubAccess);
    setSubmitButtonDisabled(true);
    setUserAttributes({
      group: userGroups,
      scoutAccess: user.hasScoutAccess,
      clubAccess: user.hasClubAccess,
    });
  };

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (isOpen) {
            setScoutAccessCheckbox(user.hasScoutAccess);
            setClubAccessCheckbox(user.hasClubAccess);
            setSubmitButtonDisabled(true);
            setUserAttributes({
              group: userGroups,
              scoutAccess: user.hasScoutAccess,
              clubAccess: user.hasClubAccess,
            });
          } else {
            resetAllToDefault();
          }
        }}>
        <DropdownMenuTrigger className="focus:outline-none focus:ring-0" disabled={loadingRow.includes(user.id)}>
          <StyledButton variant={'ghost'} className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal />
          </StyledButton>
        </DropdownMenuTrigger>
        <DropdownMenuPortal forceMount>
          <DropdownMenuContent align="end" className="text-foreground" forceMount>
            <DropdownMenuLabel>
              <h4 className=" font-normal text-base">Vereinsverwaltung</h4>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/*Group management. show all available groups */}
            <div className="p-2">
              <SelectUserGroup
                userGroups={userGroups}
                allGroups={groupList}
                onChange={(newGroups) =>
                  changeUserConfig(
                    {
                      group: newGroups,
                      scoutAccess: userAttributes.scoutAccess,
                      clubAccess: userAttributes.clubAccess,
                    },
                    user
                  )
                }
                teamName={teamName}
                isGlobalAdmin={isGlobalAdmin}
                isGroupAdmin={isGroupAdmin}
                showSelectedGroup={isGlobalAdmin}
              />
            </div>

            {/*Access Managment */}
            <div className="mt-2">
              <DropdownMenuLabel>
                <h4 className=" font-normal text-base">Zugriffsoptionen</h4>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              <div className="flex flex-col space-y-2 mt-2 p-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="scout-access"
                    checked={scoutAccessCheckbox}
                    disabled={userAssignedToAdminGroup}
                    onCheckedChange={(checked: boolean) => {
                      setScoutAccessCheckbox(checked);
                      changeUserConfig(
                        {
                          group: userAttributes.group,
                          scoutAccess: checked,
                          clubAccess: userAttributes.clubAccess,
                        },
                        user
                      );
                    }}
                    className="peer"
                  />
                  <label
                    htmlFor="scout-access"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Scouting Zugriff
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="club-access"
                    checked={clubAccessCheckbox}
                    disabled={userAssignedToAdminGroup}
                    onCheckedChange={(checked: boolean) => {
                      setClubAccessCheckbox(checked);
                      changeUserConfig(
                        {
                          group: userAttributes.group,
                          scoutAccess: userAttributes.scoutAccess,
                          clubAccess: checked,
                        },
                        user
                      );
                    }}
                    className="peer"
                  />

                  <label
                    htmlFor="club-access"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Club Controlling Zugriff
                  </label>
                </div>
              </div>
            </div>

            {/*Apply changes button */}
            <div className="flex w-full mt-4">
              <StyledButton
                className="m-auto"
                onClick={() => handleUserGroupChanges(user)}
                disabled={
                  submitButtonDisabled || loadingRow.includes(user.id) || (!scoutAccessCheckbox && !clubAccessCheckbox)
                }>
                Änderungen Übernehmen
              </StyledButton>
            </div>

            <DropdownMenuSeparator />

            {/*Delete user */}
            <div className="flex">
              <StyledButton
                className="w-full mt-4"
                variant="destructive"
                onClick={async () => {
                  setLoadingRow((prev) => [...prev, user.id]);
                  await deleteUser(user.email);
                  setLoadingRow((prev) => prev.filter((rowId) => rowId !== user.id));
                  setOpen(false);
                }}
                timeToActivate={2000}>
                Benutzer Entfernen
              </StyledButton>
            </div>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>

      <LoadingSpinner size={24} className={cn(' text-primaryColor', isRowLoading ? '' : 'opacity-0')} />
    </div>
  );
};

export default UsermanagmentDropdown;
